import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { borders } from '../tokens/borders';
import { animations } from '../tokens/animations';
import { breakpoints } from '../tokens/breakpoints';

export type StepState = 'pending' | 'active' | 'completed';

interface Step {
  id: string;
  label: string;
  description?: string;
  state: StepState;
}

interface StepperProps {
  steps: Step[];
  orientation?: 'horizontal' | 'vertical';
  showLabels?: boolean;
  showDescriptions?: boolean;
  /** When true, use vertical layout below 768px when orientation is horizontal. Default true. */
  responsive?: boolean;
  /** Accessible name for the stepper list (translated by caller). */
  ariaLabel?: string;
}

function useIsNarrow(): boolean {
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpoints.md})`).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoints.md})`);
    const handler = () => setIsNarrow(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isNarrow;
}

const CIRCLE_SIZE = 48;

const StepperContainer = styled.div<{ orientation: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-direction: ${props => props.orientation === 'vertical' ? 'column' : 'row'};
  align-items: ${props => props.orientation === 'vertical' ? 'stretch' : 'center'};
  gap: ${props => props.orientation === 'vertical' ? '16px' : '8px'};
  width: 100%;
`;

const StepItem = styled.div<{ orientation: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-direction: ${props => props.orientation === 'vertical' ? 'row' : 'column'};
  align-items: ${props => props.orientation === 'vertical' ? 'flex-start' : 'center'};
  gap: ${props => props.orientation === 'vertical' ? '16px' : '8px'};
  flex: ${props => props.orientation === 'horizontal' ? '1' : 'none'};
  position: relative;
  width: ${props => props.orientation === 'vertical' ? '100%' : 'auto'};
`;

/** Fixed-width column so the label column shares one left edge for every step. */
const CircleColumn = styled.div<{ orientation: 'horizontal' | 'vertical' }>`
  flex-shrink: 0;
  width: ${props => props.orientation === 'vertical' ? `${CIRCLE_SIZE}px` : 'auto'};
  height: ${CIRCLE_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
`;

const StepCircle = styled.div<{ state: StepState }>`
  width: ${CIRCLE_SIZE}px;
  height: ${CIRCLE_SIZE}px;
  border-radius: ${borders.radius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  transition: ${animations.transition(['background-color', 'color', 'border-color', 'box-shadow'], animations.duration.normal, animations.easing.easeOut)};
  box-sizing: border-box;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  ${props => {
    const { colors } = props.theme;
    switch (props.state) {
      case 'completed':
        return `
          background: ${colors.gradientPrimary};
          color: ${colors.surface};
          border: 3px solid ${colors.primary};
        `;
      case 'active':
        return `
          background: ${colors.primary};
          color: ${colors.surface};
          border: 3px solid ${colors.primary};
          box-shadow: 0 0 0 4px ${colors.primary}20;
        `;
      default: // pending
        return `
          background: ${colors.surface};
          color: ${colors.textSecondary};
          border: 3px solid ${colors.border};
        `;
    }
  }}
`;

const StepLine = styled.div<{ completed: boolean; orientation: 'horizontal' | 'vertical' }>`
  ${props => {
    if (props.orientation === 'horizontal') {
      return `
        position: absolute;
        top: ${CIRCLE_SIZE / 2}px;
        left: 50%;
        width: 100%;
        height: 3px;
        background: ${props.completed ? props.theme.colors.primary : props.theme.colors.border};
        z-index: 1;
        transition: ${animations.transition(['background-color'], animations.duration.normal, animations.easing.easeOut)};
      `;
    } else {
      return `
        position: absolute;
        left: ${CIRCLE_SIZE / 2}px;
        top: ${CIRCLE_SIZE}px;
        width: 3px;
        height: calc(100% + 16px);
        transform: translateX(-50%);
        background: ${props.completed ? props.theme.colors.primary : props.theme.colors.border};
        z-index: 1;
        transition: ${animations.transition(['background-color'], animations.duration.normal, animations.easing.easeOut)};
      `;
    }
  }}
`;

const LabelColumn = styled.div<{ orientation: 'horizontal' | 'vertical' }>`
  ${props =>
    props.orientation === 'vertical'
      ? `
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    /* Optical vertical align with the circle center for single-line labels */
    padding-top: 2px;
  `
      : `
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`;

const StepLabel = styled.div<{ state: StepState; orientation: 'horizontal' | 'vertical' }>`
  font-size: 12px;
  font-weight: ${props => props.state === 'active' ? '600' : '500'};
  color: ${props => {
    switch (props.state) {
      case 'active':
        return props.theme.colors.primary;
      case 'completed':
        return props.theme.colors.text;
      default:
        return props.theme.colors.textSecondary;
    }
  }};
  text-align: ${props => props.orientation === 'vertical' ? 'left' : 'center'};
  max-width: ${props => props.orientation === 'vertical' ? 'none' : '100px'};
  width: ${props => props.orientation === 'vertical' ? '100%' : 'auto'};
  line-height: 1.3;
  transition: ${animations.transition(['color', 'font-weight'], animations.duration.normal, animations.easing.easeOut)};
`;

const StepDescription = styled.div<{ state: StepState; orientation: 'horizontal' | 'vertical' }>`
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  text-align: ${props => props.orientation === 'vertical' ? 'left' : 'center'};
  max-width: ${props => props.orientation === 'vertical' ? 'none' : '120px'};
  width: ${props => props.orientation === 'vertical' ? '100%' : 'auto'};
  line-height: 1.4;
  margin-top: 4px;
`;

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.6667 5L7.50004 14.1667L3.33337 10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Stepper: React.FC<StepperProps> = ({
  steps,
  orientation = 'horizontal',
  showLabels = true,
  showDescriptions = false,
  responsive = true,
  ariaLabel,
}) => {
  const isNarrow = useIsNarrow();
  const effectiveOrientation: 'horizontal' | 'vertical' =
    responsive && orientation === 'horizontal' && isNarrow ? 'vertical' : orientation;

  return (
    <StepperContainer
      orientation={effectiveOrientation}
      role="list"
      aria-label={ariaLabel}
    >
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const stepNumber = index + 1;

        return (
          <StepItem
            key={step.id}
            orientation={effectiveOrientation}
            role="listitem"
            aria-current={step.state === 'active' ? 'step' : undefined}
          >
            {!isLast && (
              <StepLine completed={step.state === 'completed'} orientation={effectiveOrientation} />
            )}
            <CircleColumn orientation={effectiveOrientation}>
              <StepCircle
                state={step.state}
                aria-hidden="true"
              >
                {step.state === 'completed' ? (
                  <CheckIcon />
                ) : (
                  stepNumber
                )}
              </StepCircle>
            </CircleColumn>
            {showLabels && (
              <LabelColumn orientation={effectiveOrientation}>
                <StepLabel state={step.state} orientation={effectiveOrientation}>
                  {step.label}
                </StepLabel>
                {showDescriptions && step.description && (
                  <StepDescription state={step.state} orientation={effectiveOrientation}>
                    {step.description}
                  </StepDescription>
                )}
              </LabelColumn>
            )}
          </StepItem>
        );
      })}
    </StepperContainer>
  );
};
